import type { NewsSortOrder } from './newsFilters';

interface NewsFilterBarProps {
  /** How many answers the filter is holding. Zero draws no badge. */
  activeCount: number;
  /** Open the filter panel — where every narrowing decision is made. */
  onOpenFilters: () => void;
  sort: NewsSortOrder;
  onSortChange: (sort: NewsSortOrder) => void;
  /** True when a saved default was applied on load. Said quietly, once. */
  isDefaultApplied: boolean;
}

/**
 * The two controls over the list, in the toolbar above it.
 *
 * Both are client-side: sorting and filtering rearrange items the page already
 * holds, so neither fetches anything and neither spends a generation. The
 * filter itself lives in a panel rather than on the toolbar — a chip row that
 * grew a second and a third dimension stopped being readable at a glance, and
 * one button carrying a count says more in less space.
 */
export function NewsFilterBar({
  activeCount,
  onOpenFilters,
  sort,
  onSortChange,
  isDefaultApplied,
}: NewsFilterBarProps) {
  return (
    <div className="news-filter">
      <label className="news-filter__sort">
        <span className="visually-hidden">Sort the newsfeed</span>
        <select
          className="field__input news-filter__select"
          value={sort}
          onChange={(event) => onSortChange(event.target.value === 'oldest' ? 'oldest' : 'newest')}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </label>

      <button
        className={activeCount > 0 ? 'button news-filter__button--on' : 'button'}
        type="button"
        onClick={onOpenFilters}
      >
        Filter
        {activeCount > 0 ? (
          <span className="news-filter__badge">
            {activeCount}
            <span className="visually-hidden"> filters applied</span>
          </span>
        ) : null}
        {isDefaultApplied && activeCount > 0 ? (
          <span className="visually-hidden"> from your saved default</span>
        ) : null}
      </button>
    </div>
  );
}
